import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { financeAPI } from "../../../api/apiClient";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";
import "../../../css/Finance/FullRefundParticipantsSection.css";
import "../../../css/Finance/DraftParticipantAdjustmentsSection.css";

const groupParticipantsByVariant = (participants) => {
  return participants.reduce((acc, participant) => {
    const key = participant.variantGuid;
    if (!key) return acc;

    if (!acc[key]) {
      acc[key] = {
        variantGuid: participant.variantGuid,
        variantName: participant.variantName || "Bez wariantu",
        participants: [],
      };
    }

    acc[key].participants.push(participant);
    return acc;
  }, {});
};

const groupAdjustmentsByVariant = (adjustments) => {
  return adjustments.reduce((acc, item) => {
    const key = item.variantGuid;
    if (!key) return acc;

    if (!acc[key]) {
      acc[key] = {
        variantGuid: item.variantGuid,
        variantName: item.variantName || "Bez wariantu",
        adjustments: [],
      };
    }

    acc[key].adjustments.push(item);
    return acc;
  }, {});
};

const AddParticipantAdjustmentPopup = ({
  isOpen,
  availableParticipants,
  selectedParticipant,
  onSelectParticipant,
  onClose,
  onSubmit,
  savingParticipantGuid,
  isLoading,
}) => {
  const [search, setSearch] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isOpen]);

  const filteredParticipants = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return availableParticipants;

    return availableParticipants.filter((participant) => {
      const haystack = [
        participant.participantName,
        participant.reservationCode,
        participant.variantName,
        participant.primaryParticipantName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(phrase);
    });
  }, [availableParticipants, search]);

  const groupedAvailable = useMemo(() => {
    return Object.values(groupParticipantsByVariant(filteredParticipants)).sort(
      (a, b) =>
        (a.variantName || "").localeCompare(b.variantName || "", "pl", {
          sensitivity: "base",
        })
    );
  }, [filteredParticipants]);

  const numericAmount = Number(amount);
  const isAmountValid = amount !== "" && !Number.isNaN(numericAmount) && numericAmount < 0;
  const canSubmit =
    !!selectedParticipant && !!description.trim() && isAmountValid && !isLoading;

  const handleSubmit = () => {
    if (!selectedParticipant) return;
    if (!canSubmit) return;

    onSubmit(
      {
        participantGuid: selectedParticipant.participantGuid,
        amount: numericAmount,
        description: description.trim(),
        notes: notes.trim() || null,
      },
      () => {
        setAmount("");
        setDescription("");
        setNotes("");
        setSearch("");
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="full-refund-popup-overlay" onClick={onClose}>
      <div
        className="full-refund-popup adjustment-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="full-refund-popup-header">
          <div>
            <div className="full-refund-popup-title">Dodaj ręczny zwrot dla osoby</div>
            <div className="full-refund-popup-subtitle">
              Wybierz osobę, a następnie wpisz ujemną kwotę zwrotu, opis i opcjonalną notatkę.
            </div>
          </div>

          <div className="full-refund-popup-close" onClick={onClose}>
            <i className="fa-solid fa-xmark" />
          </div>
        </div>

        <div className="adjustment-popup-layout">
          <div className="adjustment-popup-people-column">
            <div className="full-refund-popup-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                type="text"
                placeholder="Szukaj po osobie, kodzie lub wariancie"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="full-refund-popup-list">
              {groupedAvailable.length === 0 ? (
                <div className="draft-empty">Brak osób do pokazania.</div>
              ) : (
                groupedAvailable.map((group) => (
                  <div key={group.variantGuid} className="full-refund-popup-group">
                    <div className="full-refund-popup-group-title">{group.variantName}</div>

                    <div className="full-refund-popup-group-list">
                      {group.participants.map((participant) => {
                        const isSaving = savingParticipantGuid === participant.participantGuid;
                        const isSelected =
                          selectedParticipant?.participantGuid === participant.participantGuid;

                        return (
                          <div
                            key={participant.participantGuid}
                            className={`full-refund-popup-row ${isSaving ? "is-saving" : ""} ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => {
                              if (!isSaving && !isLoading) {
                                onSelectParticipant(participant);
                              }
                            }}
                          >
                            <div className="full-refund-popup-row-left">
                              <div
                                className={`participant-refund-status-icon ${
                                  getParticipantStatusStyle(participant.participantStatus).statusClass
                                }`}
                                title={getParticipantStatusStyle(participant.participantStatus).label}
                              >
                                <i
                                  className={`fa-solid ${
                                    getParticipantStatusStyle(participant.participantStatus).icon
                                  }`}
                                />
                              </div>

                              <div className="full-refund-popup-row-text">
                                <div className="full-refund-popup-row-name">
                                  {participant.participantName || "—"}
                                </div>
                                <div className="full-refund-popup-row-meta">
                                  <span
                                    className={`reg-code status-${participant.reservationStatus} reg-code-small`}
                                  >
                                    {participant.reservationCode}
                                  </span>
                                  <span className="full-refund-popup-row-meta-name">
                                    {participant.primaryParticipantName || "—"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="full-refund-popup-row-right">
                              {isSelected ? (
                                <i className="fa-solid fa-check" />
                              ) : (
                                <i className="fa-solid fa-chevron-right" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="adjustment-popup-form-column">
            <div className="adjustment-form-card">
              <div className="adjustment-form-title">Wybrana osoba</div>

              {!selectedParticipant ? (
                <div className="draft-empty">
                  Najpierw wybierz osobę z listy po lewej.
                </div>
              ) : (
                <>
                  <div className="adjustment-selected-person">
                    <div className="adjustment-selected-person-name">
                      {selectedParticipant.participantName}
                    </div>
                    <div className="adjustment-selected-person-meta">
                      <span
                        className={`reg-code status-${selectedParticipant.reservationStatus} reg-code-small`}
                      >
                        {selectedParticipant.reservationCode}
                      </span>
                      <span>{selectedParticipant.primaryParticipantName || "—"}</span>
                    </div>
                  </div>

                  <div className="adjustment-form-fields">
                    <input
                      type="number"
                      step="0.01"
                      max="-0.01"
                      placeholder="Kwota zwrotu, np. -50"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />

                    {amount !== "" && !isAmountValid && (
                      <div className="adjustment-form-error">
                        W tym miejscu można dodać tylko ujemną kwotę, ponieważ sekcja służy do ręcznych zwrotów.
                      </div>
                    )}

                    <input
                      type="text"
                      placeholder="Opis zwrotu"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />

                    <input
                      type="text"
                      placeholder="Notatka (opcjonalnie)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />

                    <div
                      className={`full-refund-add-btn adjustment-submit-btn ${
                        !canSubmit ? "disabled" : ""
                      }`}
                      onClick={() => {
                        if (canSubmit) {
                          handleSubmit();
                        }
                      }}
                    >
                      {isLoading ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        <>
                          <i className="fa-solid fa-plus" />
                          <span>Dodaj zwrot</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="adjustment-form-hint">
                    W tej sekcji dodajesz tylko ręczne zwroty dla osoby, dlatego kwota musi być ujemna.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DraftParticipantAdjustmentsSection = ({
  tripGuid,
  draft,
  financialData,
  onDraftChanged,
  showToast,
  clearToast,
  canEdit
}) => {
  const { callApi, isLoading } = useApi();
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [savingParticipantGuid, setSavingParticipantGuid] = useState(null);
  const [deletingAdjustmentId, setDeletingAdjustmentId] = useState(null);

  const availableParticipants = useMemo(() => {
    const participants = [];

    (financialData?.reservations ?? []).forEach((reservation) => {
      (reservation.participants ?? []).forEach((participant) => {
        const participantGuid = participant.participantGuid || participant.guid;
        if (!participantGuid) return;

        participants.push({
          participantGuid,
          participantName: participant.name,
          participantStatus: participant.status,
          reservationGuid: reservation.reservationGuid,
          reservationCode: reservation.reservationCode,
          reservationStatus: reservation.status,
          primaryParticipantName: reservation.primaryParticipantName,
          variantGuid: reservation.variantGuid,
          variantName: reservation.variantName,
        });
      });
    });

    return participants.sort((a, b) =>
      (a.participantName || "").localeCompare(b.participantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [financialData]);

  const adjustmentsGrouped = useMemo(() => {
    const enriched = (draft?.participantAdjustments ?? []).map((item) => ({
      ...item,
      variantGuid:
        availableParticipants.find((p) => p.participantGuid === item.participantGuid)?.variantGuid ||
        item.variantGuid,
      participantStatus:
        availableParticipants.find((p) => p.participantGuid === item.participantGuid)?.participantStatus ||
        item.participantStatus,
      reservationStatus:
        availableParticipants.find((p) => p.participantGuid === item.participantGuid)?.reservationStatus ||
        item.reservationStatus,
      primaryParticipantName:
        availableParticipants.find((p) => p.participantGuid === item.participantGuid)?.primaryParticipantName ||
        item.primaryParticipantName,
    }));

    return Object.values(groupAdjustmentsByVariant(enriched)).sort((a, b) =>
      (a.variantName || "").localeCompare(b.variantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [draft, availableParticipants]);

  const handleAdd = (payload, onDone) => {
    setSavingParticipantGuid(payload.participantGuid);

    callApi(
      () => financeAPI.addDraftParticipantAdjustment(tripGuid, payload),
      async () => {
        await onDraftChanged();
        setSavingParticipantGuid(null);
        setSelectedParticipant(null);
        if (onDone) onDone();

        showToast("Dodano ręczny zwrot dla osoby", "success");
      },
      () => {
        clearToast();
        setSavingParticipantGuid(null);
      }
    );
  };

  const handleDelete = (adjustmentId) => {
    setDeletingAdjustmentId(adjustmentId);

    callApi(
      () => financeAPI.deleteDraftParticipantAdjustment(tripGuid, adjustmentId),
      async () => {
        await onDraftChanged();
        setDeletingAdjustmentId(null);
        showToast("Usunięto ręczny zwrot dla osoby", "success");
      },
      () => {
        clearToast();
        setDeletingAdjustmentId(null);
      }
    );
  };

  return (
    <div className="draft-card full-refund-picker-card">
      <div className="draft-card-title">3. Ręczne zwroty dla osób</div>
      {canEdit && <>
      <div className="draft-card-subtitle">
        W tej sekcji dodajesz wyłącznie ujemne korekty, które działają jako dodatkowy ręczny zwrot dla osoby. Osoby objęte pełnym zwrotem nie są tu dostępne.
      </div>

      <div className="full-refund-picker-toolbar">
        <div
          className={`full-refund-add-btn ${
            availableParticipants.length === 0 ? "disabled" : ""
          }`}
          onClick={() => {
            if (availableParticipants.length > 0) {
              setShowAddPopup(true);
            }
          }}
        >
          <i className="fa-solid fa-plus" />
          <span>Dodaj zwrot</span>
        </div>

        <div className="full-refund-picker-count">
          Wybrane: <strong>{draft?.participantAdjustments?.length ?? 0}</strong>
        </div>
      </div>
      </>}

      <div className="full-refund-selected-list">
        {adjustmentsGrouped.length === 0 ? (
          <div className="draft-empty">Brak ręcznych zwrotów dla osób.</div>
        ) : (
          adjustmentsGrouped.map((group) => (
            <div key={group.variantGuid} className="full-refund-selected-group">
              <div className="full-refund-selected-group-title">{group.variantName}</div>

              <div className="full-refund-selected-group-list">
                {group.adjustments.map((item) => {
                  const isDeleting = deletingAdjustmentId === item.id;
                  const participantStyle = getParticipantStatusStyle(item.participantStatus);

                  return (
                    <div
                      key={item.id}
                      className={`full-refund-selected-row adjustment-row ${
                        isDeleting ? "is-saving" : ""
                      }`}
                    >
                      <div className="full-refund-selected-row-left">
                        <div
                          className={`participant-refund-status-icon ${participantStyle.statusClass}`}
                          title={participantStyle.label}
                        >
                          <i className={`fa-solid ${participantStyle.icon}`} />
                        </div>

                        <div className="full-refund-selected-row-text">
                          <div className="full-refund-selected-row-name">
                            {item.participantName || "—"}
                          </div>

                          <div className="full-refund-selected-row-meta">
                            <span
                              className={`reg-code status-${item.reservationStatus} reg-code-small`}
                            >
                              {item.reservationCode}
                            </span>
                            <span className="full-refund-selected-row-meta-name">
                              {item.primaryParticipantName || "—"}
                            </span>
                          </div>

                          <div className="adjustment-row-description">
                            <span>{item.description}</span>
                          </div>

                          {item.notes && (
                            <div className="adjustment-row-notes">{item.notes}</div>
                          )}
                        </div>
                      </div>

                      <div className="adjustment-row-right">
                        <div className="amount-negative">
                          {Number(item.amount).toFixed(2)} zł
                        </div>

                        {canEdit && <div
                          className="full-refund-remove-icon"
                          onClick={() => {
                            if (!isDeleting && !isLoading) {
                              handleDelete(item.id);
                            }
                          }}
                        >
                          {isDeleting ? (
                            <i className="fa fa-spinner fa-spin" />
                          ) : (
                            <i className="fa-solid fa-xmark" />
                          )}
                        </div>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {canEdit && <AddParticipantAdjustmentPopup
        isOpen={showAddPopup}
        availableParticipants={availableParticipants}
        selectedParticipant={selectedParticipant}
        onSelectParticipant={setSelectedParticipant}
        onClose={() => {
          if (!savingParticipantGuid) {
            setShowAddPopup(false);
            setSelectedParticipant(null);
          }
        }}
        onSubmit={handleAdd}
        savingParticipantGuid={savingParticipantGuid}
        isLoading={isLoading}
      />
      }
    </div>
  );
};

export default DraftParticipantAdjustmentsSection;